<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="800px"
    class="custom-dialog"
    :before-close="handleClose"
  >
    <template #title>
      <h3 class="dialog-title">Import Personas</h3>
    </template>

    <!-- File Upload Section -->
    <div v-if="personas.length === 0" class="upload-section">
      <p class="upload-text">Drop a file here or click to upload</p>
      <el-upload
        class="upload-demo"
        drag
        action="#"
        :file-list="fileList"
        :on-change="handleFileChange"
        :auto-upload="false"
        :limit="1"
      >
        <i class="el-icon-upload upload-icon"></i>
        <div class="el-upload__text"><em>Click to upload</em> or drag and drop a file here</div>
      </el-upload>

      <el-input
        v-model="keyImport"
        placeholder="Enter password"
        prefix-icon="el-icon-lock"
        type="password"
        class="search-input"
        clearable
      >
        <template #append>
          <el-button
            type="primary"
            @click="importPersona"
            :loading="isLoading"
            :disabled="isLoading"
          >
            Unlock File
          </el-button>
        </template>
      </el-input>
    </div>

    <!-- Persona Selection Section -->
    <div v-else class="persona-selection">
      <p class="selection-text">Select personas to import</p>
      <!-- Search -->
      <div style="display: flex; gap: 10px; align-items: center; margin-bottom: 10px">
        <el-input v-model="searchQuery" placeholder="Search personas..." clearable />
      </div>
      <el-table
        :data="filteredPersonas"
        @selection-change="handleSelectionChange"
        style="width: 100%; max-height: 300px; overflow-y: auto"
        border
      >
        <!-- Checkbox column -->
        <el-table-column type="selection" width="55" reserve-selection> </el-table-column>

        <!-- Name column -->
        <el-table-column prop="name" label="Name" sortable :sort-method="caseInsensitiveSort">
        </el-table-column>

        <!-- Created At column -->
        <el-table-column prop="createdAt" label="Created At" width="200" sortable>
          <template #default="{ row }">
            {{ formattedDate(row.createdAt) }}
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Footer Buttons -->
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose" type="danger" :loading="isLoading" :disabled="isLoading">
          Cancel
        </el-button>
        <el-button
          type="primary"
          @click="handleConfirm"
          :disabled="!selectedPersonas.length || isLoading"
          :loading="isLoading"
        >
          Import ({{ selectedPersonas.length }})
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import moment from "moment";

export default {
  name: "ExportDialog",
  props: ["isLoading"],
  data() {
    return {
      dialogVisible: false,
      fileList: [],
      personas: [],
      selectedPersonas: [],
      keyImport: "",
      searchQuery: "",
      areAllSelected: false,
      algo: {
        name: "AES-GCM",
        iv: new TextEncoder("utf-8").encode("keyreply"),
      },
      uploadedFile: null,
    };
  },
  methods: {
    handleConfirm() {
      const selectedPersonas = this.personas.filter((p) => this.selectedPersonas.includes(p.id));
      this.$emit("import", selectedPersonas);
    },
    handleFileChange(file) {
      this.uploadedFile = file;
      return false;
    },
    handleFileRead(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          if (Array.isArray(data)) {
            this.personas = data;
          } else {
            this.$message.error("Invalid JSON format");
          }
        } catch (error) {
          this.$message.error("Error parsing JSON file");
        }
      };
      reader.readAsText(file);
    },
    handleResetState() {
      this.fileList = [];
      this.personas = [];
      this.selectedPersonas = [];
    },
    handleClose() {
      this.dialogVisible = false;
      this.handleResetState();
    },
    async importPersona() {
      if (!this.keyImport) {
        this.$notify.error({
          title: "Password Error",
          position: "bottom-right",
          message: "Invalid password",
        });

        return;
      }
      if (!this.uploadedFile) {
        this.$notify.error({
          title: "File Error",
          position: "bottom-right",
          message: "No file uploaded.",
        });

        return;
      }

      try {
        const key = await this.importPrivateKey(this.keyImport);
        var fr = new FileReader();

        fr.readAsArrayBuffer(this.uploadedFile.raw);
        const cipherBuffer = await new Promise((resolve) => {
          fr.onloadend = (event) => {
            resolve(event.target.result);
          };
        });
        const clearBuffer = await crypto.subtle.decrypt(this.algo, key, cipherBuffer);
        const clearJSON = new TextDecoder("utf-8").decode(clearBuffer);

        const importData = JSON.parse(clearJSON);
        if (importData.exportType !== "persona") {
          this.$notify.error({
            title: "File Error",
            position: "bottom-right",
            message: "Invalid file type.",
          });
          return;
        }
        this.personas = importData.data;
      } catch (error) {
        console.error(error.message);
        this.$notify.error({
          title: "Decryption Error",
          position: "bottom-right",
          message: "Please check the file and password.",
        });
      }
    },
    async importPrivateKey(keyImportObj) {
      return await window.crypto.subtle.importKey(
        "jwk",
        {
          alg: "A256GCM",
          ext: true,
          k: keyImportObj,
          key_ops: ["decrypt"],
          kty: "oct",
        },
        {
          name: "AES-GCM",
          length: 256,
        },
        false,
        ["decrypt"]
      );
    },
    toggleSelectAll() {
      if (this.areAllSelected) {
        this.selectedPersonas = [];
      } else {
        this.selectedPersonas = this.personas.map((p) => p.id);
      }
      this.areAllSelected = !this.areAllSelected;
    },
    handleSelectionChange(selectedRows) {
      this.selectedPersonas = selectedRows.map((row) => row.id);
    },
    formattedDate(date) {
      return moment(date).format("YYYY-MM-DD HH:mm:ss");
    },
    caseInsensitiveSort(a, b) {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    },
  },
  computed: {
    filteredPersonas() {
      return this.personas.filter((persona) =>
        persona.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
  },
};
</script>

<style scoped>
.custom-dialog {
  border-radius: 8px;
}

.dialog-title {
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
}

.upload-section {
  text-align: center;
  padding: 20px;
}

.upload-text {
  font-size: 14px;
  margin-bottom: 10px;
}

.upload-demo {
  width: 100%;
  border-radius: 8px;
}

.upload-icon {
  font-size: 24px;
  color: #409eff;
}

.persona-selection {
  padding: 10px 20px;
}

.selection-text {
  font-size: 14px;
  margin-bottom: 10px;
  font-weight: bold;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.persona-checkbox {
  font-size: 14px;
  margin: 5px 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  padding: 10px;
}

.dialog-footer .el-button {
  min-width: 100px;
}
</style>
